/* eslint no-restricted-imports: 0 */
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid"
import { CustomIcon } from "../../Icons/CustomIcons"
import { useRef } from "react"
import { mergeProps, useButton, useFocusRing, AriaButtonOptions } from "react-aria"
import { classNames } from "../../../../utils/classNames"
import { DateRangePickerValue } from "../types"
import { getFormattedDateRangeValue, moveDateRange } from "../utils"
import { IsoDate } from "@figurepos/fgr-lib-business-logic/lib/common/dateTime/types"

type Props = Omit<AriaButtonOptions<"button">, "value"> & {
    isPickerOpen: boolean
    value: DateRangePickerValue
    minDate: IsoDate | null
    maxDate: IsoDate | null
    onChange: (value: DateRangePickerValue) => void
}

export const DateRangePickerInput = (props: Props) => {
    const ref = useRef(null)
    const { value, minDate, maxDate, ...buttonOpts } = props
    const { buttonProps } = useButton(buttonOpts as AriaButtonOptions<"button">, ref)
    const { focusProps } = useFocusRing()

    const { start: startFormatted, end: endFormatted } = getFormattedDateRangeValue(value)

    const moveRangeArrowClicked = (direction: "back" | "forward") => {
        const newDateRange = moveDateRange(direction, value, minDate, maxDate)
        if (!newDateRange) {
            return
        }
        props.onChange({ start: newDateRange.start, end: newDateRange.end })
    }

    return (
        <div
            data-testid="date-range-picker"
            className={classNames(classes.wrap, props.isPickerOpen && classes.wrapOpen)}>
            <button type="button" onClick={() => moveRangeArrowClicked("back")} className={classes.arrowButton}>
                <ChevronLeftIcon className={classes.arrowIconLeft} />
            </button>
            <button {...mergeProps(buttonProps, focusProps)} ref={ref} type="button" className={classes.datesButton}>
                <input
                    className={classes.input}
                    readOnly={true}
                    value={startFormatted ?? ""}
                    placeholder="MM/DD/YYYY"
                />
                <div className={classes.separator}>-</div>
                <input className={classes.input} readOnly={true} value={endFormatted ?? ""} placeholder="MM/DD/YYYY" />
                <CustomIcon icon="calendar" className={classes.calendarIcon} />
            </button>
            <button type="button" onClick={() => moveRangeArrowClicked("forward")} className={classes.arrowButton}>
                <ChevronRightIcon className={classes.arrowIconRight} />
            </button>
        </div>
    )
}

const classes = {
    wrap: "flex items-center border border-gray-300 rounded-md bg-white h-[32px] hover:[&:not(.arrow-button)]:border-primary-color",
    wrapOpen: "border-primary-color",
    arrowButton:
        "arrow-button p-0 m-0 h-full px-1.5 flex items-center justify-center cursor-pointer bg-transparent hover:text-primary-color transition outline-none",
    arrowIconLeft: "w-[20px] h-auto -mt-px ml-[-2px]",
    arrowIconRight: "w-[20px] h-auto mr-[-2px] -mt-px",
    calendarIcon: "-mt-px fill-black/80 w-[20px] h-auto pl-1.5 transition opacity-60",
    separator: "px-2 -mt-px",
    datesButton:
        "px-4 flex h-full flex-1 justify-center items-center p-0 m-0 cursor-pointer bg-transparent border-x border-gray-200 outline-none",
    input: "w-[86px] pointer-events-none",
}
